import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import "./Home.css";

import personalInjuryIcon from "./assets/personal_injury_icon.png";

import attorneyPhotoCircle from "./assets/edgar-lopez-law.png";
import attorneyPhotoWide from "./assets/edgar-lopez-law-1.png";

function Home() {
  const pageRef = useRef(null);

  useEffect(() => {
    // Reveal on scroll
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("visible");
          else entry.target.classList.remove("visible");
        });
      },
      { threshold: 0.2 }
    );

    const sections = document.querySelectorAll(".reveal");
    sections.forEach((sec) => observer.observe(sec));

    // Parallax vars (mobile-friendly): expose scroll position to CSS
    const el = pageRef.current;
    let ticking = false;

    const setVars = () => {
      if (!el) return;
      el.style.setProperty("--scrollY", String(window.scrollY || 0));
      el.style.setProperty("--vh", String(window.innerHeight || 0));
      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(setVars);
      }
    };

    // init + listeners
    setVars();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", setVars);

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", setVars);
    };
  }, []);

  return (
    <div className="home-container" ref={pageRef}>
      <Helmet>
        <title>Edgar Lopez Law | Personal Injury Attorney</title>
        <meta
          name="description"
          content="Edgar Lopez Law is a personal injury law office representing accident and injury victims. Explore our practice focus, meet the attorney, and request a free consultation."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="hero-section">
        <video
          className="hero-video"
          autoPlay
          loop
          muted
          playsInline
          poster="/logo512.png"
        >
          <source src="/banner.mp4" type="video/mp4" />
          Your browser does not support HTML5 video.
        </video>
      </section>

      {/* Hero Bridge Video (fills mobile gap) */}
      <section className="hero-bridge" aria-hidden="true">
        <video className="hero-bridge-video" autoPlay loop muted playsInline>
          <source src="/bridge.mp4" type="video/mp4" />
          Your browser does not support HTML5 video.
        </video>
      </section>

      {/* Practice Areas */}
      <section className="practice-areas-section reveal">
        <h2 className="practice-areas-title">Our Practice Areas</h2>

        <div className="practice-areas-grid">
          <Link to="/about#car-accidents" className="practice-card-link">
            <article className="practice-card">
              <img
                className="practice-icon"
                src={personalInjuryIcon}
                alt="Car Accidents"
              />
              <h3 className="practice-card-title">CAR ACCIDENTS</h3>
              <p className="practice-card-text">
                If you were injured in a crash, we help you pursue compensation
                for medical bills, lost income, and pain and suffering—while
                dealing with the insurance company so you can focus on recovery.
              </p>
            </article>
          </Link>

          <Link to="/about#motorcycle-accidents" className="practice-card-link">
            <article className="practice-card">
              <img
                className="practice-icon"
                src={personalInjuryIcon}
                alt="Motorcycle Accidents"
              />
              <h3 className="practice-card-title">MOTORCYCLE ACCIDENTS</h3>
              <p className="practice-card-text">
                Motorcycle injuries are often serious. We build strong cases with
                clear documentation and advocacy to protect riders from unfair
                blame and lowball offers.
              </p>
            </article>
          </Link>

          <Link to="/about#slip-falls" className="practice-card-link">
            <article className="practice-card">
              <img
                className="practice-icon"
                src={personalInjuryIcon}
                alt="Slip and Fall"
              />
              <h3 className="practice-card-title">SLIP &amp; FALLS</h3>
              <p className="practice-card-text">
                Property owners have a duty to keep premises reasonably safe. If
                dangerous conditions caused your fall, we investigate quickly and
                preserve evidence to support your claim.
              </p>
            </article>
          </Link>

          <Link to="/about#rideshare" className="practice-card-link">
            <article className="practice-card">
              <img
                className="practice-icon"
                src={personalInjuryIcon}
                alt="Rideshare Accidents"
              />
              <h3 className="practice-card-title">RIDESHARE (UBER / LYFT)</h3>
              <p className="practice-card-text">
                Rideshare claims can involve multiple insurance policies. Whether
                you were a passenger, driver, or hit by a rideshare vehicle, we
                help identify coverage and pursue the right parties.
              </p>
            </article>
          </Link>
        </div>

        <div className="practice-areas-callout">
          <span className="practice-areas-callout-label">GIVE US A CALL</span>
          <a className="practice-areas-callout-phone" href="tel:+19517492948">
            (951) 749-2948
          </a>
        </div>
      </section>

      {/* Attorney Section */}
      <section className="attorney-section reveal">
        <h2 className="attorney-title">Attorney Edgar Lopez</h2>

        <div className="attorney-grid">
          <div className="attorney-photo-wrap">
            <img
              src={attorneyPhotoCircle}
              alt="Attorney Edgar Lopez"
              className="attorney-photo-circle"
            />
          </div>

          <div className="attorney-bio">
            <p>
              Edgar Lopez grew up in Los Angeles County before moving to
              Beaumont in 2005 with his family. After graduating from Beaumont
              High School he attended Cal State San Bernardino where he
              received his undergraduate degree in Political Science.
            </p>
            <p>
              He then attended California Western School of Law in San Diego
              where he received his law degree.
            </p>
            <p>
              He has worked for small and large law firms, and today his practice
              is focused on representing personal injury clients. His main focus is
              to be a zealous advocate for the people he serves.
            </p>
            <p>El abogado Edgar Lopez habla español con fluidez.</p>
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="core-values-section reveal">
        <div className="core-values-grid">
          <div className="core-values-photo-wrap">
            <img
              src={attorneyPhotoWide}
              alt="Edgar Lopez Law"
              className="core-values-photo"
            />
          </div>

          <div className="core-values-content">
            <div className="lang-toggle">Español | English</div>
            <h2 className="core-values-title">Our Core Values</h2>
            <p className="core-values-text">
              The Law Office of Edgar Lopez is more than just a legal practice.
              We are your dedicated and unwavering legal advocate, committed to
              standing by your side from the very beginning of your case until
              its successful resolution. With our strong expertise and
              unwavering determination, we will fight tirelessly to protect
              your rights and secure the best possible outcome for you. Trust
              us to be your staunch legal ally throughout your entire journey.
            </p>

            <a className="core-values-btn" href="/personal-injury">
              What We Do
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;